<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import { mapGetters, mapActions, mapState } from "vuex";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import Swal from "sweetalert2";
import moment from "moment";
import Loader from "@/components/widgets/preloader";

export default {
  page: {
    title: "Delivery Note Details",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },
  components: {
    Layout,
    PageHeader,
    Loader,
  },
  data() {
    return {
      title: "Delivery Note Details",
      items: [
        { text: "Home", href: "/" },
        { text: "Receipt", active: true },
      ],
      data: {
        id: atob(this.$route.params.id),
      },
      isGeneratingPdf: false,
      whatsappTargetPhone: "",
      standard: 0,
      exempt: 0,
      zero: 0,
    };
  },
  computed: {
    ...mapState("notification", ["type", "message", "show"]),
    ...mapGetters("saledetails", ["Sales", "getSaleDetails"]),
    ...mapGetters("setting", [
      "PaymentOptions",
      "ReceiptSettings",
      "GeneralSettings",
    ]),
    sale() {
      if (
        this.getSaleDetails &&
        Array.isArray(this.getSaleDetails.data) &&
        this.getSaleDetails.data.length
      ) {
        return this.getSaleDetails.data[0];
      }

      if (this.Sales && !Array.isArray(this.Sales)) {
        return this.Sales;
      }

      return null;
    },
    rows() {
      return this.sale && Array.isArray(this.sale.items)
        ? this.sale.items.length
        : 1;
    },
    logoSource() {
      if (
        this.sale &&
        this.sale.business2 &&
        this.sale.business2.Businesslogo_base64
      ) {
        return this.sale.business2.Businesslogo_base64;
      }
      if (
        this.sale &&
        this.sale.business2 &&
        this.sale.business2.Businesslogo
      ) {
        return this.sale.business2.Businesslogo;
      }
      return null;
    },

    visibleColumns() {
      let count = 5; // Start with base columns
      if (
        this.GeneralSettings.show_batch_selling == 1 &&
        this.hasAnyData("batch_number")
      )
        count++;
      if (
        this.ReceiptSettings.show_expiry_date == 1 &&
        this.hasAnyData("expiry_date")
      )
        count++;
      if (
        this.ReceiptSettings.show_manufacturing_date == 1 &&
        this.hasAnyData("manufacture_date")
      )
        count++;
      if (
        this.ReceiptSettings.show_description == 1 &&
        this.hasAnyData("description")
      )
        count++;
      return count;
    },
  },

  watch: {
    show(newValue) {
      if (newValue == true) {
        if (this.type == "success") {
          Swal.fire("Success!", this.message.msg, "success");
        } else {
          Swal.fire("Error!", this.message.msg, "error");
        }
      }
    },
    sale(newValue) {
      if (newValue) {
        this.calcTot();
      }
    },
  },

  methods: {
    ...mapActions({
      FetchSales: "saledetails/fetchSaleDetails",
      FetchModes: "setting/fetchPaymentOptions",
      notifySuccess: "notification/success",
      notifyError: "notification/error",
      fetchReceiptSettings: "setting/fetchReceiptSettings",
      fetchGeneralSettings: "setting/fetchGeneralSettings",
    }),

    thousandSeperator(m) {
      if (m !== null && m !== undefined) {
        return m
          .toString()
          .replace(/\D/g, "")
          .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      }
      return m;
    },

    UpdateSetting(id, status) {
      let activeoption = this.PaymentOptions.find((item) => item.id == id);
      if (activeoption) {
        activeoption.showstatus = status === 1 ? 0 : 1;
      }
    },

    formatPhoneNumber(phone) {
      if (!phone) return null;
      let dialcode = this.sale.business2.country.dial_code;
      return dialcode + phone.toString().replace(/\D/g, "").slice(-9);
    },

    async generateAndSharePdf(shareType) {
      this.isGeneratingPdf = true;
      const saleElement = document.getElementById("sale-content-wrapper");

      if (!saleElement) {
        this.notifyError({ msg: "Error: Could not find sale content." });
        this.isGeneratingPdf = false;
        return;
      }

      try {
        const canvas = await html2canvas(saleElement, {
          scale: 2,
          useCORS: true,
          ignoreElements: (element) =>
            element.classList.contains("d-print-none"),
        });

        const imgData = canvas.toDataURL("image/jpeg");
        const pdf = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

        pdf.addImage(imgData, "JPEG", 0, 0, pdfWidth, pdfHeight);
        const saleFileName = `Delivery-note-${this.sale.receipt}-${
          this.sale.customerobj.name
        }-${moment(new Date()).format("YYYY-MM-DD")}.pdf`;

        if (shareType === "app") {
          const pdfBlob = pdf.output("blob");
          const file = new File([pdfBlob], saleFileName, {
            type: "application/pdf",
          });
          if (navigator.share && navigator.canShare({ files: [file] })) {
            await navigator.share({
              title: `Delivery Note ${this.sale.receipt}`,
              text: `Hello ${this.sale.customerobj.name},\n\nPlease find your delivery note attached.\n\nThank you!`,
              files: [file],
            });
          }
        } else if (shareType === "email") {
          pdf.save(saleFileName);
          const successMessage =
            "The delivery note has been downloaded as " +
            saleFileName +
            ". Please attach this file to your message.";
          this.notifySuccess({ msg: successMessage });
          const customerEmail = this.sale.customerobj.email;
          const subject = `Sale ${this.sale.receipt} from ${this.sale.business2.name}`;
          const body = `Hello ${this.sale.customerobj.name},\n\nPlease find your delivery note attached.\n\nThank you!`;
          window.open(
            `mailto:${customerEmail || ""}?subject=${encodeURIComponent(
              subject,
            )}&body=${encodeURIComponent(body)}`,
          );
        } else if (shareType === "whatsapp") {
          pdf.save(saleFileName);
          const successMessage =
            "The delivery note has been downloaded as " +
            saleFileName +
            ". Please attach this file to your message.";
          this.notifySuccess({ msg: successMessage });
          const customerPhone = this.sale.customerobj.phone;
          this.whatsappTargetPhone = this.formatPhoneNumber(customerPhone);
          if (this.whatsappTargetPhone) {
            this.$bvModal.show("share-whatsapp-modal");
          } else {
            this.notifyError({
              msg: "Cannot share via WhatsApp because the customer does not have a contact number saved.",
            });
          }
        }
      } catch (error) {
        this.notifyError({
          msg:
            "An error occurred while generating the PDF. Please ensure you are connected to the internet." +
            error,
        });
      } finally {
        this.isGeneratingPdf = false;
      }
    },

    shareToWhatsApp(type) {
      const message = `Hello, please find attached your sale receipt ${this.sale.receipt}.`;
      const encodedMessage = encodeURIComponent(message);
      let url = "";
      if (this.whatsappTargetPhone) {
        url =
          type === "app"
            ? `whatsapp://send?phone=${this.whatsappTargetPhone}&text=${encodedMessage}`
            : `https://web.whatsapp.com/send?phone=${this.whatsappTargetPhone}&text=${encodedMessage}`;
        if (type === "app") {
          window.location.href = url;
        } else {
          window.open(url, "_blank");
        }
      }
      this.$bvModal.hide("share-whatsapp-modal");
    },

    hasAnyData(fieldKey) {
      if (!this.sale || !this.sale.items) return false;
      return this.sale.items.some((item) => {
        if (fieldKey === "expiry_date")
          return item.batch && item.batch.expiry_date;
        if (fieldKey === "manufacture_date")
          return item.batch && item.batch.manufacture_date;
        if (fieldKey === "description") return item.description;
        if (fieldKey === "batch_number")
          return item.batch && item.batch.batch_number;
        return false;
      });
    },

    calcTot() {
      let x1 = 0;
      let x2 = 0;
      let x3 = 0;
      if (this.sale && this.sale.items) {
        this.sale.items.forEach((item) => {
          if (item.taxcategory == "01") {
            x1 += parseInt(item.SPrice * item.qty);
          }
          if (item.taxcategory == "02") {
            x3 += parseInt(item.SPrice * item.qty);
          }
          if (item.taxcategory == "03") {
            x2 += parseInt(item.SPrice * item.qty);
          }
        });
        this.standard = x1;
        this.exempt = x2;
        this.zero = x3;
      }
    },
  },
  created() {
    try {
      this.FetchSales(atob(this.$route.params.id));
      this.FetchModes();
      this.fetchReceiptSettings();
      this.fetchGeneralSettings();
    } catch (error) {
      this.notifyError({ msg: "Failed to initialize sale details: " + error });
    }
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" class="d-print-none" />
    <div v-if="isGeneratingPdf" class="pdf-loading-overlay">
      <div class="spinner-border text-light" role="status"></div>
      <span class="text-light ms-2">Generating PDF...</span>
    </div>
    <div class="row mt-5 px-2" v-if="sale">
      <div class="col-lg-12">
        <div class="card px-3" id="sale-content-wrapper">
          <div class="card-body" id="sale-content">
            <div class="sale-title">
              <div class="row d-print-none mb-3">
                <div class="col-12 text-end">
                  <div class="btn-group me-2">
                    <b-dropdown
                      id="deliverynote-share-dropdown"
                      variant="info"
                      right
                    >
                      <template #button-content>
                        <i class="fa fa-share-alt"></i> Share
                        <i class="mdi mdi-chevron-down ms-2"></i>
                      </template>

                      <b-dropdown-item
                        @click.prevent="generateAndSharePdf('app')"
                      >
                        <i class="fa fa-desktop me-2"></i> Any App
                      </b-dropdown-item>

                      <b-dropdown-item
                        @click.prevent="generateAndSharePdf('email')"
                      >
                        <i class="fa fa-envelope me-2"></i> Email
                      </b-dropdown-item>

                      <b-dropdown-item
                        @click.prevent="generateAndSharePdf('whatsapp')"
                      >
                        <i class="fab fa-whatsapp me-2"></i> WhatsApp
                      </b-dropdown-item>
                    </b-dropdown>
                  </div>
                  <a
                    href="javascript:window.print()"
                    class="btn btn-success waves-effect waves-light me-2"
                  >
                    <i class="fa fa-print"></i> Print
                  </a>
                  <b-button @click="$router.go(-1)" variant="warning"
                    ><i class="fa fa-arrow-left"></i> Back</b-button
                  >
                </div>
              </div>
              <div class="row">
                <div class="col-md-3" align="center">
                  <img
                    v-if="logoSource"
                    :src="logoSource"
                    height="80"
                    crossorigin="anonymous"
                  />
                </div>
                <div class="col-md-9 text-center">
                  <span class="font-size-24 text-uppercase text-success"
                    ><strong>{{
                      sale ? sale.business2.name : ""
                    }}</strong></span
                  ><br />
                  <span class="font-size-18"
                    ><b>{{ sale ? sale.branch.address : "" }}</b></span
                  ><br />
                  <span class="font-size-18"
                    ><strong>Tel: </strong
                    ><b>{{ sale ? sale.branch.phone1 : "" }}</b></span
                  ><br />
                  <span class="font-size-18" v-if="sale && sale.branch.email"
                    ><strong>Email: </strong
                    ><b>{{ sale ? sale.branch.email : "---" }}</b></span
                  ><br />
                </div>
              </div>
            </div>
            <div style="border-top: 1px solid black"></div>
            <div class="font-size-24 text-center py-3">
              <b>DELIVERY NOTE</b>
            </div>
            <div class="row font-size-17 py-3">
              <div class="col-6">
                <address>
                  <strong>Customer:</strong><br />{{
                    sale ? sale.customerobj.name : ""
                  }}
                  - {{ sale ? sale.customerobj.phone : "" }}
                </address>
              </div>
              <div class="col-6">
                <address>
                  <strong>Date:</strong> {{ sale ? sale.date : "" }}
                  <br /><strong>Delivery note Number: </strong>
                  {{ sale ? sale.receipt : "" }}
                </address>
              </div>
            </div>
            <div style="border-top: 1px solid black"></div>
            <div class="row font-size-14 py-3">
              <div class="col-6">
                <div v-if="sale.staff || sale.vehicle_number">
                  <p><strong>Delivered By:</strong> {{ sale.staff || "-----" }}</p>
                  <p><strong>Vehicle Number:</strong> {{ sale.vehicle_number || "-----" }}</p>
                </div>
              </div>
              <div class="col-6">
                <div v-if="sale.business2.vatstatus == 1 || (sale.customerobj && sale.customerobj.tin)">
                  <p><strong>Tax Details:</strong></p>
                  <p v-if="sale.business2.tin"><strong>TIN:</strong> {{ sale.business2.tin }}</p>
                  <p v-if="sale.customerobj && sale.customerobj.tin"><strong>Customer TIN:</strong> {{ sale.customerobj.tin }}</p>
                </div>
              </div>
            </div>
            <div style="border-top: 1px solid black"></div>
            <div>
              <table class="table font-size-15">
                <thead>
                  <tr>
                    <th style="width: 70px">No.</th>
                    <th>Product</th>
                    <th class="text-sm-end">Quantity</th>
                    <th
                      class="text-sm-end"
                      v-if="
                        GeneralSettings.show_batch_selling == 1 &&
                        hasAnyData('batch_number')
                      "
                    >
                      Batch
                    </th>
                    <th
                      class="text-sm-end"
                      v-if="
                        ReceiptSettings.show_expiry_date == 1 &&
                        hasAnyData('expiry_date')
                      "
                    >
                      Expiry
                    </th>
                    <th
                      class="text-sm-end"
                      v-if="
                        ReceiptSettings.show_manufacturing_date == 1 &&
                        hasAnyData('manufacture_date')
                      "
                    >
                      Mfg
                    </th>
                    <th
                      class="text-sm-end"
                      v-if="
                        ReceiptSettings.show_description == 1 &&
                        hasAnyData('description')
                      "
                    >
                      Desc
                    </th>
                    <th class="text-sm-end">Price</th>
                    <th class="text-sm-end">Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(pdt, index) in sale ? sale.items : []"
                    :key="index"
                  >
                    <td>{{ index + 1 }}</td>
                    <td>{{ pdt.product }}</td>
                    <td class="text-sm-end">{{ pdt.qty }} {{ pdt.usymbol }}</td>
                    <td
                      class="text-sm-end"
                      v-if="
                        GeneralSettings.show_batch_selling == 1 &&
                        hasAnyData('batch_number')
                      "
                    >
                      {{ pdt.batch ? pdt.batch.batch_number : "" }}
                    </td>
                    <td
                      class="text-sm-end"
                      v-if="
                        ReceiptSettings.show_expiry_date == 1 &&
                        hasAnyData('expiry_date')
                      "
                    >
                      {{ pdt.batch ? pdt.batch.expiry_date : "" }}
                    </td>
                    <td
                      class="text-sm-end"
                      v-if="
                        ReceiptSettings.show_manufacturing_date == 1 &&
                        hasAnyData('manufacture_date')
                      "
                    >
                      {{ pdt.batch ? pdt.batch.manufacture_date : "" }}
                    </td>
                    <td
                      class="text-sm-end"
                      v-if="
                        ReceiptSettings.show_description == 1 &&
                        hasAnyData('description')
                      "
                    >
                      {{ pdt.description }}
                    </td>
                    <td class="text-sm-end">
                      {{ pdt.SPrice.toLocaleString() }}
                    </td>
                    <td class="text-sm-end">
                      {{ pdt.total_price.toLocaleString() }}
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr v-if="sale.business2.vatstatus == 1" class="summary-row">
                    <td :colspan="visibleColumns">
                      <span class="label font-weight-bold">TAX DETAILS</span>
                    </td>
                  </tr>
                  <tr v-if="sale.business2.vatstatus == 1 && standard > 0" class="summary-row">
                    <td :colspan="visibleColumns" class="font-size-13">
                      <span class="label">Standard (18%): </span>
                      <span class="value">Net: {{ (standard / 1.18).toFixed(2).toLocaleString() }}</span> |
                      <span class="value">Tax: {{ (standard - standard / 1.18).toFixed(2).toLocaleString() }}</span> |
                      <span class="value">Gross: {{ standard.toLocaleString() }}</span>
                    </td>
                  </tr>
                  <tr v-if="sale.business2.vatstatus == 1 && zero > 0" class="summary-row">
                    <td :colspan="visibleColumns" class="font-size-13">
                      <span class="label">Zero (0%): </span>
                      <span class="value">{{ zero.toLocaleString() }}</span>
                    </td>
                  </tr>
                  <tr v-if="sale.business2.vatstatus == 1 && exempt > 0" class="summary-row">
                    <td :colspan="visibleColumns" class="font-size-13">
                      <span class="label">Exempt (-): </span>
                      <span class="value">{{ exempt.toLocaleString() }}</span>
                    </td>
                  </tr>
                  <tr v-if="sale.business2.vatstatus == 1" class="summary-row">
                    <td :colspan="visibleColumns">
                      <span class="label">NET AMOUNT: </span>
                      <span class="value">{{ (exempt + zero + standard / 1.18).toFixed(2).toLocaleString() }}</span>
                    </td>
                  </tr>
                  <tr v-if="sale.business2.vatstatus == 1" class="summary-row">
                    <td :colspan="visibleColumns">
                      <span class="label">TAX AMOUNT: </span>
                      <span class="value">{{ (standard - standard / 1.18).toFixed(2).toLocaleString() }}</span>
                    </td>
                  </tr>
                  <tr class="summary-row">
                    <td :colspan="visibleColumns">
                      <span class="label">TOTAL: </span>
                      <span class="value"
                        >{{ sale ? sale.currency : "" }}
                        {{ sale ? sale.totalAmt.toLocaleString() : 0 }}</span
                      >
                    </td>
                  </tr>
                  <tr class="summary-row">
                    <td :colspan="visibleColumns">
                      <span class="label">PAID: </span>
                      <span class="value"
                        >{{ sale ? sale.currency : "" }}
                        {{ sale ? sale.totalPaid.toLocaleString() : 0 }}</span
                      >
                    </td>
                  </tr>
                  <tr class="summary-row">
                    <td :colspan="visibleColumns" v-if="sale && sale.discount">
                      <span class="label">DISCOUNT: </span>
                      <span class="value"
                        >{{ sale ? sale.currency : "" }}
                        {{ sale ? sale.discount.toLocaleString() : 0 }}</span
                      >
                    </td>
                  </tr>
                  <tr class="summary-row total-row">
                    <td :colspan="visibleColumns">
                      <span class="label">BALANCE: </span>
                      <span class="value"
                        >{{ sale ? sale.currency : "" }}
                        {{
                          sale
                            ? (
                                sale.totalAmt -
                                (sale.totalPaid - sale.discount)
                              ).toLocaleString()
                            : 0
                        }}</span
                      >
                    </td>
                  </tr>
                  <tr
                    class="summary-row total-row"
                    v-if="
                      ReceiptSettings.indicate_customerbalance == 1 &&
                      sale &&
                      sale.customerobj &&
                      sale.customerobj.name
                    "
                  >
                    <td :colspan="visibleColumns">
                      <span class="label">TOTAL BALANCE DUE: </span>
                      <span class="value"
                        >{{ sale ? sale.currency : "" }}
                        {{
                          sale && sale.customerobj
                            ? (-sale.customerobj.balance || 0).toLocaleString()
                            : "0"
                        }}</span
                      >
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div class="footer-details text-center">
              <p v-if="ReceiptSettings.indicate_user == 1">
                <b>Served By:</b> {{ sale ? sale.user : "-----" }}
              </p>
              <p v-if="ReceiptSettings.indicate_goods_vat_inclusive == 1">
                <b>Prices inclusive of VAT where applicable</b>
              </p>
              <p v-if="ReceiptSettings.indicate_goods_not_returnable == 1">
                <b>Goods once sold are not returnable</b>
              </p>
              <p>
                <b>{{
                  sale && sale.business2 ? sale.business2.tagline : "-----"
                }}</b>
              </p>
              <p v-if="ReceiptSettings.indicate_goods_vat_inclusive == 1">
                <b>Prices inclusive of VAT where applicable</b>
              </p>
              <p v-if="ReceiptSettings.indicate_goods_not_returnable == 1">
                <b>Goods once sold are not returnable</b>
              </p>
              <p>
                <b>{{
                  sale && sale.business2 ? sale.business2.tagline : "-----"
                }}</b>
              </p>
              <p><b>THIS IS A DELIVERY NOTE NOT A TAX INVOICE</b></p>
              <div style="border-top: 1px solid black"></div>
              <p class="print-info">
                Printed via <br />
                www.poscream.com
              </p>
            </div>
          </div>
          <!-- modal share sale via WhatsApp -->
          <b-modal
            header-bg-variant="info"
            header-text-variant="light"
            body-text-variant="info"
            footer-bg-variant="info"
            footer-text-variant="info"
            id="share-whatsapp-modal"
            hide-footer
          >
            <template #modal-title> Share Sale Via WhatsApp</template>
            <div class="modal-content">
              <div class="modal-body">
                <p>The sale receipt PDF has been downloaded to your device.</p>
                <p class="mb-0">
                  How would you like to open WhatsApp to share it?
                </p>
              </div>
              <div class="modal-footer d-flex justify-content-center">
                <button
                  type="button"
                  class="btn btn-success"
                  @click="shareToWhatsApp('app')"
                >
                  <i class="fab fa-whatsapp me-2"></i>Open WhatsApp App
                </button>
                <button
                  type="button"
                  class="btn btn-secondary"
                  @click="shareToWhatsApp('web')"
                >
                  <i class="fa fa-desktop me-2"></i>Use WhatsApp Web
                </button>
              </div>
            </div>
          </b-modal>
          <!-- modal ends -->
        </div>
      </div>
    </div>
    <div class="card-body p-5 text-center" v-else>
      <Loader />
      <p class="mt-3">Fetching data, please wait...</p>
    </div>
  </Layout>
</template>

<style>
#sale-content tfoot .summary-row td {
  text-align: right !important;
  border: none !important;
  padding: 4px 8px !important;
  font-size: 13pt; /* Your preferred font size */
}
/* Style for the bolded total/balance rows */
#sale-content tfoot .total-row {
  font-size: 1.05em; /* Makes the font slightly larger */
}
.label {
  font-weight: bold;
}

.footer-details p {
  font-size: 15px;
  margin-bottom: 3px;
  text-align: center;
}

/* --- STYLES FOR AUTOMATIC PRINT FORMATTING --- */
@media print {
  * {
    box-sizing: border-box;
  }
  .left-side-menu,
  .navbar-custom,
  .footer,
  .page-header,
  .d-print-none {
    display: none !important;
  }
  .payment-option-hidden-on-print {
    display: none !important;
  }

  body {
    background-color: #fff !important;
  }
  .main-content,
  .page-content,
  .container-fluid {
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
  }
  #sale-content-wrapper {
    border: none !important;
    box-shadow: none !important;
    padding: 0 !important;
  }
}
/* --- FORMAT 1: Standard A4/A3/A5 Styles --- */
@media print and (min-width: 150mm) {
  @page {
    margin: 15mm;
  }
  #sale-content {
    font-size: 10pt !important;
  }
  #sale-content .font-size-24 {
    font-size: 18pt !important;
  }
  #sale-content .font-size-18 {
    font-size: 12pt !important;
  }
  #sale-content .table tr {
    page-break-inside: avoid;
  }
  /* The global styles handle alignment. We only need to adjust font size if necessary. */
  #sale-content tfoot .summary-row {
    font-size: 11pt !important;
  }
  #sale-content tfoot .total-row {
    font-size: 12pt !important;
  }
}

/* --- FORMAT 2: Receipt Printer Styles --- */
@media print and (max-width: 100mm) {
  @page {
    margin: 0;
  }
  #sale-content {
    width: 100% !important;
    padding: 0 2mm !important;
    font-family: "Courier New", Courier, monospace !important;
    font-size: 9pt !important;
    color: #000 !important;
    overflow-wrap: break-word;
    word-wrap: break-word;
  }
  #sale-content .row:not(.d-print-none) {
    display: block !important;
  }
  #sale-content [class*="col-"]:not(.d-print-none) {
    width: 100% !important;
    padding: 0 !important;
    text-align: left !important;
  }
  #sale-content .text-center {
    text-align: center !important;
  }
  #sale-content .text-sm-end {
    text-align: right !important;
  }
  #sale-content img {
    max-width: 100% !important;
    height: auto !important;
    margin-bottom: 5px;
  }
  #sale-content .font-size-24 {
    font-size: 12pt !important;
    font-weight: bold;
  }
  #sale-content .font-size-18 {
    font-size: 10pt !important;
    line-height: 1.2;
  }
  #sale-content h3 {
    font-size: 10pt !important;
    font-weight: bold;
  }

  #sale-content .table {
    font-size: 8pt !important;
    table-layout: fixed;
    width: 100%;
    border-collapse: collapse;
  }
  #sale-content .table th,
  #sale-content .table td {
    padding: 3px 2px !important;
    border-bottom: 1px dotted #000 !important;
    border-right: 1px dotted #000;
    word-wrap: break-word;
  }
  #sale-content .table th:first-child,
  #sale-content .table td:first-child {
    width: 10% !important; /* Force a small width for the first column */
    border-left: 1px dotted #000;
  }

  /* Summary (tfoot) Section for Receipt */
  #sale-content tfoot .summary-row td {
    border-top: 1px solid #000 !important;
    border-bottom: none !important;
    border-left: none !important;
    border-right: none !important;
    padding: 5px 2px !important;
    font-size: 10pt !important;
  }
  /* This makes the label and value appear next to each other inside the single cell */
  #sale-content tfoot .summary-row span {
    display: inline;
  }

  #sale-content .table thead tr {
    border-bottom: 1px solid #000 !important;
  }
  #sale-content div[style*="border-top"] {
    border-top: 1px dashed black !important;
    margin: 5px 0 !important;
  }
  #sale-content .py-5,
  #sale-content .py-3,
  #sale-content .p-2,
  #sale-content .py-4 {
    padding-top: 5px !important;
    padding-bottom: 5px !important;
  }
}
</style>
